import { Ref, forwardRef } from "react"
import { Form, FormikProps } from "formik"
import { Formik } from "../../../../../shared/components/forms/SchemaFormik"
import * as yup from "yup"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { CreateOrEditShopFormValues, ShopItem, ShopTypes } from "../shopsTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { selectUserType } from "../../../../login/loginSelectors"
import { useAppSelector } from "../../../../store/storeHooks"
import { UserType } from "../../../../login/loginTypes"
import { TabsForm } from "./formElements/TabForms"
import { createHardwareOrder } from "../shopsApi"

type CreateUpdateShopFormProps = Readonly<{
    initialValues: CreateOrEditShopFormValues
    onSubmit: (formData: ShopItem) => void
}>

type FormikRef = Ref<FormikProps<CreateOrEditShopFormValues>>

export const CreateUpdateShopForm = forwardRef(
    ({ initialValues, onSubmit }: CreateUpdateShopFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const actualUserType = useAppSelector(selectUserType)
        const isUserTypeSupport = actualUserType === UserType.SUPPORT

        const validationSchema = yup.object({
            name: yup.string().required(l10n.getString("required-field")),
            type: yup.string().required(l10n.getString("required-field")),
        })

        const { mutate: callCreateHardwareOrder } = useApiMutation(createHardwareOrder, {})

        function handleSubmit({ hardwarePackage, type, deadline, posDetails, ...rest }: CreateOrEditShopFormValues) {
            hardwarePackage?.length &&
                callCreateHardwareOrder({
                    order: {
                        deliveries: [
                            {
                                orderLines: hardwarePackage,
                            },
                        ],
                    },
                })

            const modifyData = {
                type: type!,
                ...(deadline
                    ? {
                          deadline: {
                              purchases: {
                                  ...deadline.purchases,
                                  timeOfDay: deadline.purchases.timeOfDay.toFormat("HH:mm"),
                              },
                              cancellation: {
                                  ...deadline.cancellation,
                                  timeOfDay: deadline.cancellation.timeOfDay.toFormat("HH:mm"),
                              },
                          },
                      }
                    : {}),
                ...(type === ShopTypes.POS
                    ? {
                          posDetails: {
                              defaultLanguage: posDetails?.defaultLanguage,
                              ...(isUserTypeSupport
                                  ? {
                                        ...posDetails,
                                    }
                                  : {}),
                          },
                      }
                    : {}),
                ...rest,
            }
            onSubmit(modifyData)
        }

        return (
            <Formik<CreateOrEditShopFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
                validateOnBlur={false}
                validateOnChange={false}
            >
                <Form>
                    <TabsForm />
                </Form>
            </Formik>
        )
    },
)
